type TQueueEntry = {
  task: () => Promise<unknown>;
  signal?: AbortSignal;
};

/**
 * Runs async tasks with a bounded number running at once.
 *
 * Tasks can be registered at any time via `add`; each starts as soon as a slot
 * frees up. Passing a signal ties that task to a cancellation: once the signal
 * aborts, the task is skipped instead of started, without consuming a slot. The
 * task itself still owns what it does with the signal while running — handing it
 * to a request, guarding its side effects after an await.
 *
 * A rejecting task never escapes as an unhandled rejection: its error goes to
 * `onError` when provided, and is swallowed otherwise.
 */
export class ConcurrencyQueue {
  private readonly concurrency: number;
  private readonly onError?: (error: unknown) => void;
  private readonly pending: TQueueEntry[] = [];
  private running = 0;

  constructor(concurrency: number, onError?: (error: unknown) => void) {
    this.concurrency = Math.max(1, concurrency);
    this.onError = onError;
  }

  /** Registers a task, starting it immediately if a slot is free. An already-aborted task is dropped. */
  add(task: () => Promise<unknown>, signal?: AbortSignal): void {
    if (signal?.aborted) {
      return;
    }
    this.pending.push({ task, signal });
    this.startNext();
  }

  /** Drops every not-yet-started task. Tasks already running are left to settle. */
  clear(): void {
    this.pending.length = 0;
  }

  /** Number of tasks waiting or currently running. */
  get size(): number {
    return this.pending.length + this.running;
  }

  private startNext(): void {
    while (this.running < this.concurrency && this.pending.length > 0) {
      const entry = this.pending.shift();
      if (!entry) {
        return;
      }
      if (entry.signal?.aborted) {
        continue;
      }
      this.running++;
      // The catch must come first: it settles the chain, so `finally` can no
      // longer re-throw the rejection into an unhandled promise rejection.
      void entry
        .task()
        .catch((error) => this.onError?.(error))
        .finally(() => {
          this.running--;
          this.startNext();
        });
    }
  }
}
